import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PublicLayout } from "@/components/Navbar";
import { StockBadge } from "@/components/StockBadge";
import { Bell, Package, RefreshCw, Trash2, Inbox, FileText } from "lucide-react";

const typeMeta = {
  STOCK_ALERT: { icon: Package, label: "Back in Stock", cls: "text-emerald-400" },
  REORDER_REMINDER: { icon: RefreshCw, label: "Reorder Reminder", cls: "text-[#D4AF37]" },
  ORDER_RECEIPT: { icon: FileText, label: "Order Receipt", cls: "text-sky-400" },
};

export default function RetailerAlerts() {
  const [notifs, setNotifs] = useState([]);
  const [watch, setWatch] = useState([]);

  const load = () => {
    api.get("/retailer/notifications").then(({ data }) => setNotifs(data));
    api.get("/retailer/watchlist").then(({ data }) => setWatch(data));
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const unwatch = async (sku) => {
    await api.delete(`/retailer/watchlist/${sku}`);
    toast.success("Removed from watchlist");
    load();
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-[#D4AF37]" />
          <h1 className="font-serif-display text-4xl font-bold text-slate-50">Alerts & <span className="mc-gold-gradient">Watchlist</span></h1>
        </div>
        <p className="mt-2 text-sm text-[#94A3B8]">Back-in-stock alerts and reorder reminders. We'll email you the moment a watched line is available again.</p>

        <div className="mt-10 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-slate-100">Your Notifications</h2>
            {notifs.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-[#2A2E39] p-10 text-center text-[#64748B]">
                <Inbox className="mx-auto h-9 w-9 opacity-40" /><p className="mt-3">No notifications yet.</p>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {notifs.map((n) => {
                  const m = typeMeta[n.type] || typeMeta.STOCK_ALERT;
                  return (
                    <div key={n.id} data-testid={`notif-${n.id}`} className="flex gap-4 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-4">
                      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/5 ${m.cls}`}><m.icon className="h-4 w-4" /></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-100">{n.title}</span>
                          <span className="text-[11px] text-[#64748B]">{new Date(n.created_at).toLocaleString("en-IE")}</span>
                        </div>
                        <p className="mt-1 text-sm text-[#94A3B8]">{n.body}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${m.cls}`}>{m.label}</span>
                          {n.sku && <Link to="/portal" className="text-[11px] text-[#D4AF37] hover:underline">Order now →</Link>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-lg font-semibold text-slate-100">Watchlist</h2>
            {watch.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-[#2A2E39] p-6 text-center text-sm text-[#64748B]">
                Not watching anything. Tap “Notify me” on any out-of-stock product.
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {watch.map((w) => (
                  <div key={w.sku} data-testid={`watch-${w.sku}`} className="flex items-center gap-3 rounded-xl border border-[#2A2E39] bg-[#1A1D24] p-3">
                    <img src={w.image_url} alt={w.name} className="h-12 w-12 rounded-lg object-cover" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-100">{w.name}</div>
                      <StockBadge status={w.stock_status} testId={`watch-stock-${w.sku}`} />
                    </div>
                    <button data-testid={`unwatch-${w.sku}`} onClick={() => unwatch(w.sku)} className="rounded-lg p-2 text-[#94A3B8] hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
